/**
 * Login Page
 * Elegant landing page styled similar to vofs.se
 * Partner Login in top right corner
 */

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { useAuthStore } from "../stores/auth-store";
import { loginRequest } from "../config/msal-config";
import {
  isMicrosoftConfigured,
  isGoogleConfigured,
} from "../config/google-config";
import { getCalendarAvailability, getTrips } from "../services/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

// Use environment variable for API URL, fallback to relative /api for local dev
const API_BASE = import.meta.env.VITE_API_URL || "/api";

export default function LoginPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { login } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { instance: msalInstance } = useMsal();

  /**
   * Prefetch calendar and trip data to speed up initial page load
   * This runs in the background after successful login
   */
  const prefetchCalendarData = useCallback(() => {
    const today = new Date();
    const startDate = format(startOfMonth(today), "yyyy-MM-dd");
    const endDate = format(endOfMonth(addMonths(today, 2)), "yyyy-MM-dd");

    // Prefetch in parallel - don't await, let it happen in background
    queryClient.prefetchQuery({
      queryKey: ["calendar-availability", startDate, endDate],
      queryFn: () => getCalendarAvailability({ startDate, endDate }),
      staleTime: 2 * 60 * 1000,
    });

    queryClient.prefetchQuery({
      queryKey: ["trips"],
      queryFn: getTrips,
      staleTime: 10 * 60 * 1000,
    });
  }, [queryClient]);

  /**
   * Send OAuth token to backend for verification and get session
   */
  const authenticateWithBackend = useCallback(
    async (provider: "google" | "microsoft", accessToken: string) => {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, accessToken }),
      });

      // Handle empty responses safely
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            data.message ||
            `Authentication failed (${response.status})`,
        );
      }

      return data.data;
    },
    [],
  );

  /**
   * Handle redirect result from Microsoft login
   * Uses polling to handle timing issues with main.tsx setting the sessionStorage
   */
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const checkForRedirectResult = async () => {
      const storedResult = sessionStorage.getItem("msal_redirect_result");
      if (storedResult) {
        sessionStorage.removeItem("msal_redirect_result");
        try {
          const { accessToken } = JSON.parse(storedResult);
          if (accessToken) {
            setIsLoading(true);
            const { token, user, refreshToken } = await authenticateWithBackend(
              "microsoft",
              accessToken,
            );
            // Clear any cached data from previous user session
            queryClient.clear();
            login(token, user, refreshToken);
            // Prefetch calendar data in background for faster page load
            prefetchCalendarData();
            navigate(user.role === "crew" ? "/logbook" : "/dashboard");
          }
        } catch (err) {
          console.error("Error processing redirect result:", err);
          setError(err instanceof Error ? err.message : t("login.errors.loginFailed"));
        } finally {
          setIsLoading(false);
        }
        return true; // Found and processed
      }
      return false; // Not found
    };

    const pollForResult = async () => {
      const found = await checkForRedirectResult();
      if (!found && attempts < maxAttempts) {
        attempts++;
        setTimeout(pollForResult, 100); // Check every 100ms for up to 1 second
      }
    };

    pollForResult();
  }, [authenticateWithBackend, login, navigate]);

  /**
   * Handle Microsoft login via MSAL redirect flow
   * Multi-tenant: works with any Azure AD organization
   * Uses redirect flow for better compatibility with Azure Static Web Apps
   */
  const handleMicrosoftLogin = useCallback(async () => {
    if (!isMicrosoftConfigured()) {
      setError(t("login.errors.microsoftNotConfigured"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Clear any stale interaction state before redirect
      sessionStorage.removeItem("msal.interaction.status");
      Object.keys(sessionStorage).forEach((key) => {
        if (
          key.includes("msal.") &&
          (key.includes("nonce") ||
            key.includes("state") ||
            key.includes("interaction"))
        ) {
          sessionStorage.removeItem(key);
        }
      });
      // Use redirect flow - more reliable with Azure Static Web Apps
      await msalInstance.loginRedirect(loginRequest);
      // Will redirect, loading state will be reset when page returns
    } catch (err: unknown) {
      console.error("Microsoft login error:", err);
      setError(t("login.errors.microsoftLoginFailed"));
      setIsLoading(false);
    }
  }, [msalInstance, t]);

  /**
   * Handle Google login success
   * Google OAuth is inherently multi-tenant
   */
  const handleGoogleSuccess = useCallback(
    async (credentialResponse: CredentialResponse) => {
      setIsLoading(true);
      setError(null);

      try {
        if (!credentialResponse.credential) {
          throw new Error(t("login.errors.noGoogleCredential"));
        }

        // Send ID token to backend for verification
        const { token, user, refreshToken } = await authenticateWithBackend(
          "google",
          credentialResponse.credential,
        );
        // Clear any cached data from previous user session
        queryClient.clear();
        login(token, user, refreshToken);
        // Prefetch calendar data in background for faster page load
        prefetchCalendarData();
        navigate(user.role === "crew" ? "/logbook" : "/dashboard");
      } catch (err) {
        console.error("Google login error:", err);
        setError(err instanceof Error ? err.message : t("login.errors.googleLoginFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [authenticateWithBackend, login, navigate, queryClient, t],
  );

  /**
   * Handle Google login error
   */
  const handleGoogleError = useCallback(() => {
    setError(t("login.errors.googleLoginFailed"));
  }, [t]);

  const handleLocalLogin = useCallback(async (role: "admin" | "crew") => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/auth/dev-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || `Login failed (${response.status})`);
      queryClient.clear();
      login(result.data.token, result.data.user, result.data.refreshToken);
      navigate(role === "crew" ? "/logbook" : "/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("login.errors.loginFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate, queryClient, t]);

  return (
    <div className="min-h-screen relative">
      {/* Hero Background - Full screen with overlay using vofs.se imagery */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,30,60,0.6), rgba(0,40,80,0.75)), url('https://www.vofs.se/public/katieca-sunset-sea.jpeg')`,
        }}
      />

      {/* Navigation Header */}
      <header className="relative z-10">
        <nav className="flex items-center justify-between px-6 md:px-12 py-4">
          {/* Logo - Vision of Stockholm */}
          <a
            href="https://www.vofs.se"
            className="flex items-center space-x-3 hover:opacity-90 transition-opacity"
          >
            <img
              src="/images/Logo_Positiv_VoS.png"
              alt="Vision of Stockholm"
              className="h-10 md:h-12 brightness-0 invert"
            />
          </a>

          {/* Contact Info - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-6 text-white/80 text-sm">
            <a
              href="mailto:info@vofs.se"
              className="hover:text-white transition-colors"
            >
              info@vofs.se
            </a>
            <span>+46 70 561 75 75</span>
            <LanguageSwitcher variant="dark" />
          </div>

          {/* Partner Login Button */}
          <button
            onClick={() => setShowLoginModal(true)}
            className="px-5 py-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded hover:bg-white/20 transition-all duration-300 text-sm font-medium"
          >
            {t("login.partnerLogin")}
          </button>
        </nav>
      </header>

      {/* Hero Content */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-6 text-center">
        <h1 className="font-serif text-4xl md:text-6xl font-light text-white tracking-wider mb-4">
          VISION OF STOCKHOLM
        </h1>
        <div className="w-24 h-0.5 bg-white/50 mb-6" />
        <h2 className="font-serif text-xl md:text-2xl text-white/90 font-light tracking-widest uppercase mb-8">
          {t("login.tagline")}
        </h2>
        <p className="max-w-2xl text-white/80 text-lg md:text-xl leading-relaxed mb-12">
          {t("login.description")}
        </p>

        {/* CTA Button */}
        <button
          onClick={() => setShowLoginModal(true)}
          className="px-8 py-3 bg-white text-gray-900 rounded hover:bg-gray-100 transition-all duration-300 font-medium tracking-wide"
        >
          {t("login.partnerLogin")}
        </button>

        {/* Social Links */}
        <div className="flex items-center space-x-4 mt-8">
          <a
            href="https://www.instagram.com/visionofstockholm"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </a>
          <a
            href="https://www.facebook.com/visionofstockholm"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 absolute bottom-0 left-0 right-0 py-4 text-center text-white/50 text-sm">
        <p>{t("login.footer")}</p>
      </footer>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowLoginModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-200">
            {/* Close button */}
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center mb-8">
              <img
                src="/images/Logo_Positiv_VoS.png"
                alt="Vision of Stockholm"
                className="h-10 mx-auto mb-4"
              />
              <h3 className="text-2xl font-light text-gray-900 tracking-wide">
                {t("login.partnerLogin")}
              </h3>
              <p className="text-gray-500 mt-2">
                {t("login.modalSubtitle")}
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-6 text-sm">
                {error}
              </div>
            )}

            {/* Login buttons */}
            <div className="space-y-4">
              {import.meta.env.DEV && <div className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs text-amber-900">{t("login.localOnly")}</p>
                <button className="btn btn-secondary w-full" disabled={isLoading} onClick={() => void handleLocalLogin("admin")}>{t("login.localAdmin")}</button>
                <button className="btn btn-secondary w-full" disabled={isLoading} onClick={() => void handleLocalLogin("crew")}>{t("login.localCrew")}</button>
              </div>}
              {/* Google Login */}
              {isGoogleConfigured() ? (
                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="outline"
                    size="large"
                    width="100%"
                    text="continue_with"
                  />
                </div>
              ) : (
                <button
                  disabled
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-100 cursor-not-allowed opacity-50"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="font-medium text-gray-500">
                    {t("login.googleNotConfigured")}
                  </span>
                </button>
              )}

              {/* Microsoft Login */}
              <button
                onClick={handleMicrosoftLogin}
                disabled={isLoading || !isMicrosoftConfigured()}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
                <span className="font-medium text-gray-700">
                  {isMicrosoftConfigured()
                    ? t("login.continueWithMicrosoft")
                    : t("login.microsoftNotConfiguredShort")}
                </span>
              </button>
            </div>

            {/* Info text */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                {t("login.infoText")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
